import { Box, CircularProgress } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import parseMoneyValue from '@utils/parseMoneyValue';
import React, { ReactNode } from 'react';
import { AreaChart, Area, ResponsiveContainer, Tooltip } from 'recharts';
import parseDate from '@utils/parseDate';

type Props = {
  label: string;
  title: string;
  chartData?: ChartData[];
  profitIndicator?: ReactNode;
  customHelpTooltip?: ReactNode;
  isMoneyChart?: boolean;
  isLoading: boolean;
};
export function SalesChart(props: Props) {
  const {
    title,
    chartData,
    profitIndicator,
    customHelpTooltip,
    isLoading = true,
    isMoneyChart = false,
  } = props;
  const label = isMoneyChart ? parseMoneyValue(props.label) : props.label;

  return (
    <div className="flex flex-col shadow-default rounded-default">
      {isLoading && (
        <div className="flex justify-center items-center h-[164px]">
          <CircularProgress />
        </div>
      )}
      {!isLoading && (
        <>
          <div className="p-md">
            <div className="flex items-center">
              <Typography className="text-gray-400">{title}</Typography>
              <div className="flex-1 flex items-center justify-between">
                {customHelpTooltip}
                {profitIndicator}
              </div>
            </div>
            <Typography className="text-[#374151] text-3xl font-bold">
              {label || 0}
            </Typography>
          </div>

          <ResponsiveContainer height={60} className="flex-1">
            <AreaChart data={chartData} margin={{ top: 5, bottom: 5 }}>
              <defs>
                <linearGradient
                  id="gradient-chart"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1.5"
                >
                  <stop offset="20%" stopColor="#2E3192" stopOpacity={1} />
                  <stop offset="80%" stopColor="#FFFFFF" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Tooltip
                content={
                  <CustomTooltip
                    title={title}
                    data={chartData}
                    isMoneyChart={isMoneyChart}
                  />
                }
              />
              <Area
                dataKey="value"
                stroke="#2E3192"
                strokeWidth={1.5}
                fill="url(#gradient-chart)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

type ChartData = {
  value: number | string;
  date: string;
};
const CustomTooltip = ({
  active,
  payload,
  title,
  isMoneyChart = false,
}: {
  active?: boolean;
  data: any;
  payload?: { payload: ChartData }[];
  title: string;
  isMoneyChart: boolean;
}) => {
  if (active && payload && payload.length) {
    return (
      <Box>
        <Box>
          {payload.map(({ payload }, idx: number) => {
            const date = parseDate(payload.date, {
              type: 'date',
              convertToLocale: false,
            });

            let value = payload.value;
            if (isMoneyChart) {
              value = parseMoneyValue(value) || 0;
            }
            return (
              <Box
                key={idx}
                style={{
                  display: 'inline-block',
                  padding: 10,
                  borderRadius: '5px',
                }}
                className="bg-primary/80"
              >
                <Typography className="text-white text-xs">{`Data ${date}`}</Typography>
                <Typography className="text-white text-xs">{`${title}: ${value}`}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  }

  return null;
};
